/**
 * 편집 정책 검색 store
 * Ipv4, Ipv6
 *
 * @since 2018-06-05
 * @author kdydesign.kim
 */

import {
  CommonApiPrefix as cmnApiPrefix,
  SecutiryPolicyApiPrefix as objApiPrefix,
  CmApiPrefix as cmApiPrefix
} from 'utils'

// utils
import {pcDownloadFile} from '~/utils/files'
import {clientDate, formatDate} from '~/utils/date'

// 방화벽 정책 내용 검색 데이터
const initFwSearchContentsData = () => {
  return {
    use: 2,
    src: [],
    src_operator: 1,
    src_case: '0000',
    user: void 0,
    user_case: '000',
    dst: [],
    dst_operator: 1,
    dst_case: '0000',
    app: void 0,
    app_case: '00',
    service: [],
    srv_operator: 1,
    service_case: '0000',
    action: 5,
    direction: 3,
    vlan: null,
    etc: void 0,
    etc_case: '00',
    desc: void 0,
    desc_case: '00',
    create_dt: `${clientDate('YYYYMMDD')}${clientDate('YYYYMMDD')}`,
    // TAMS
    ip_ver_type: 1, // ipv4: 1, ipv6: 2
    fw_rule_id: void 0,
    fw_rule_name: '',
    fw_rule_name_case: '00'
  }
}

// NAT 정책 내용 검색 데이터
const initNatSearchContentsData = () => {
  return {
    inf: 'None',
    src: [],
    src_operator: 1,
    src_case: '0000',
    dst: [],
    dst_operator: 1,
    dst_case: '0000',
    service: [],
    srv_operator: 1,
    service_case: '0000',
    desc: void 0,
    desc_case: '00',
    create_dt: `${clientDate('YYYYMMDD')}${clientDate('YYYYMMDD')}`
  }
}

// shadow 방화벽 정책 검색 데이터
const initSearchShadowData = () => {
  return {
    fw_rule_id: void 0,
    unuse_rule: 0,
    tm_check: 0
  }
}

// shadow NAT 정책 검색 데이터
const initSearchNatShadowData = () => {
  return {
    nat_rule_id: void 0,
    unuse_rule: 0
  }
}

// 시간 만료 정책 검색
const initSearchTimeoutData = () => {
  return {
    tm_type: 1,
    expired: 0,
    unuse_rule: 0,
    expired_date: clientDate('YYYY-MM-DD')
  }
}

// TAMS
const initSearchCommonData = () => {
  return {
    device_search_type: 0,
    device_text: '',
    device_id: []
  }
}

const initSearchFwRuleIdMap = () => {
  return {
    contents: {},
    timeout: {},
    shadow: {},
    natContents: {}
  }
}

const makeSearchRuleIdMapInfo = ({ searchData = [], pickList = [] } = {}) => {
  const ruleIdMap = {}
  _.map(searchData, row => {
    if (!_.get(ruleIdMap, [row.fw_grp_id])) {
      ruleIdMap[row.fw_grp_id] = {}
    }

    if (_.get(pickList, ['length'], 0) > 0) {
      ruleIdMap[row.fw_grp_id][row.fw_rule_id] = _.pick(row, pickList)
    } else {
      ruleIdMap[row.fw_grp_id][row.fw_rule_id] = row
    }
  })
  return ruleIdMap
}

export const state = () => ({
  searchCommonData: initSearchCommonData(), // TAMS
  searchTimeoutData: initSearchTimeoutData(),
  searchFwShadowData: initSearchShadowData(),
  searchNatShadowData: initSearchNatShadowData(),
  searchFwContentsData: initFwSearchContentsData(),
  searchNatContentsData: initNatSearchContentsData(),
  searchContentDataList: [],
  searchObjectDataList: [],
  searchNatContentDataList: [],
  searchNatObjectDataList: [],
  searchTimeoutDataList: [],
  searchShadowDataList: [],
  selectSearchFilterData: void 0,
  editSearchDataList: void 0,
  editSearchType: void 0,
  isPolicyContentCreateDt: false,
  // TAMS
  searchFwRuleIdMap: initSearchFwRuleIdMap()
  // Mock
  // searchFwRuleIdMap: {
  //   2: { fw_rule_id: 2 },
  //   3: { fw_rule_id: 3 },
  //   14: { fw_rule_id: 14 },
  //   26: { fw_rule_id: 26 }
  // }
})

export const mutations = {
  // store 초기화
  clearEditPolicySearchStore (state) {
    // TAMS
    state.searchCommonData = initSearchCommonData()
    state.searchFwRuleIdMap = initSearchFwRuleIdMap()

    state.searchTimeoutData = initSearchTimeoutData()
    state.searchFwShadowData = initSearchShadowData()
    state.searchNatShadowData = initSearchNatShadowData()
    state.searchFwContentsData = initFwSearchContentsData()
    state.searchNatContentsData = initNatSearchContentsData()
    state.searchContentDataList = []
    state.searchObjectDataList = []
    state.searchNatContentDataList = []
    state.searchNatObjectDataList = []
    state.searchTimeoutDataList = []
    state.searchShadowDataList = []
    state.selectSearchFilterData = void 0
    state.editSearchDataList = void 0
    state.editSearchType = void 0
    state.isPolicyContentCreateDt = false
  },

  // 편집 정책 초기화
  resetSearchEditData (state) {
    // TAMS
    state.editSearchType = void 0
    state.searchFwRuleIdMap = initSearchFwRuleIdMap()
    state.searchCommonData = initSearchCommonData()

    state.searchTimeoutData = initSearchTimeoutData()
    state.searchFwShadowData = initSearchShadowData()
    state.searchNatShadowData = initSearchNatShadowData()
    state.searchFwContentsData = initFwSearchContentsData()
    state.searchNatContentsData = initNatSearchContentsData()
    state.searchContentDataList = []
    state.searchObjectDataList = []
    state.searchNatContentDataList = []
    state.searchNatObjectDataList = []
    state.searchShadowDataList = []
    state.searchTimeoutDataList = []
    state.selectSearchFilterData = void 0
    state.isPolicyContentCreateDt = false
  },

  // 편집 정책 속성 별 데이터 초기화
  resetSearchEditUnitData (state, attr) {
    state.searchFwContentsData[attr] = initFwSearchContentsData()[attr]
    state.searchNatContentsData[attr] = initNatSearchContentsData()[attr]
  },

  // 편집 정책 검색 결과 초기화
  resetSearchEditResultData (state) {
    state.searchNatContentDataList = []
    state.searchNatObjectDataList = []
    state.searchContentDataList = []
    state.searchObjectDataList = []

    state.selectSearchFilterData = void 0
  },

  // 편집 정책 속성 별 데이터 설정
  setSearchEditUnitData (state, {attr, data}) {
    if (_.isArray(state.searchFwContentsData[attr])) {
      state.searchFwContentsData[attr].push(data)
    } else {
      state.searchFwContentsData[attr] = data
    }

    if (_.isArray(state.searchNatContentsData[attr])) {
      state.searchNatContentsData[attr].push(data)
    } else {
      state.searchNatContentsData[attr] = data
    }
  },

  // shadow 데이터 설정
  setShadowData (state, {ruleId, ruleType, unUseRule, tmCheck, type}) {
    if (ruleType === 'fw') {
      if (type === 'ruleId') {
        state.searchFwShadowData.fw_rule_id = ruleId
      }

      if (type === 'unuse') {
        state.searchFwShadowData.unuse_rule = unUseRule
      }

      if (type === 'tmCheck') {
        state.searchFwShadowData.tm_check = tmCheck
      }
    }

    if (ruleType === 'nat') {
      if (type === 'ruleId') {
        state.searchNatShadowData.nat_rule_id = ruleId
      }

      if (type === 'unuse') {
        state.searchNatShadowData.unuse_rule = unUseRule
      }

      if (type === 'tmCheck') {
        state.searchNatShadowData.tm_check = tmCheck
      }
    }
  },

  // 편집 정책 검색 - 방화벽 정책 내용 검색
  searchFwContentsPolicy (state, data) {
    const searchContentDataList = _.map(data.rule, 'fw_rule')

    state.editSearchType = 'contents'
    state.searchContentDataList = Object.freeze(searchContentDataList)
    state.searchObjectDataList = Object.freeze(data.obj)

    // TAMS
    const ruleMap = makeSearchRuleIdMapInfo({ searchData: state.searchContentDataList })
    state.searchFwRuleIdMap[state.editSearchType] = Object.freeze(ruleMap)
  },

  // 편집 정책 검색 - NAT 정책 내용 검색
  searchNatContentsPolicy (state, data) {
    state.editSearchType = 'natContents'
    state.searchNatContentDataList = Object.freeze(data.rule)
    state.searchNatObjectDataList = Object.freeze(data.obj)

    // TAMS
    const ruleMap = makeSearchRuleIdMapInfo({ searchData: state.searchNatContentDataList })
    state.searchFwRuleIdMap[state.editSearchType] = Object.freeze(ruleMap)
  },

  // 편집 정책 검색 - 시간 만료 검색
  searchTimeoutPolicy (state, data) {
    state.editSearchType = 'timeout'

    // 탐스 미적용
    // let searchTimeoutDataList = _.map(data.rule, (rule) => {
    //   return _.assign(rule, {
    //     disableRule: 0,
    //     fw_rule_id: Number(rule.fw_rule_id)
    //   })
    // })
    // state.searchTimeoutDataList = Object.freeze(searchTimeoutDataList)

    // TAMS
    state.searchTimeoutDataList = Object.freeze(data.rule)
    const ruleMap = makeSearchRuleIdMapInfo({ searchData: state.searchTimeoutDataList })
    state.searchFwRuleIdMap[state.editSearchType] = Object.freeze(ruleMap)
  },

  // 편집 정책 검색 - shadow 정책 검색
  searchShadowPolicy (state, {result}) {
    state.editSearchType = 'shadow'

    // 탐스 미적용
    // let searchShadowDataList = _.map(result, (rule) => {
    //   if (ruleType === 'fw') {
    //     return _.assign(rule, {
    //       fw_rule_id: Number(rule.fw_rule_id)
    //     })
    //   }
    //
    //   if (ruleType === 'nat') {
    //     return _.assign(rule, {
    //       nat_rule_id: Number(rule.nat_rule_id)
    //     })
    //   }
    // })

    state.searchShadowDataList = Object.freeze(result.rule)
    // TAMS
    const ruleMap = makeSearchRuleIdMapInfo({ searchData: result.rule })
    state.searchFwRuleIdMap[state.editSearchType] = Object.freeze(ruleMap)
  },

  // 편집 정책 검색 - 정책 정책 옵션 Bit 설정
  setContentOpts (state, {data, key}) {
    let result = []

    if (key === 'src_case' || key === 'dst_case' || key === 'service_case') {
      result = [0, 0, 0, 0]
    } else if (key === 'user_case') {
      result = [0, 0, 0]
    } else if (key === 'app_case' || key === 'etc_case' || key === 'desc_case' || key === 'fw_rule_name_case') { // TAMS (fw_rule_name_case)
      result = [0, 0]
    }

    _.each(data, (o) => {
      result.splice(o, 1, 1)
    })

    state.searchFwContentsData[key] = state.searchNatContentsData[key] = _.join(result, '')
  },

  // 편집 정책 검색 결과 초기화
  resetEditSearchDataList (state) {
    state.editSearchDataList = void 0
  },

  // 선택된 검색 데이터 선택
  selectSearchFilterData (state, data) {
    state.selectSearchFilterData = data
  },

  // 검색 결과 수정(정책 비활성화, 정책 삭제)
  updateSearchData (state, data) {
    let dataList = []

    if (state.editSearchType === 'contents') {
      dataList = _.cloneDeep(state.searchContentDataList)
    }

    if (state.editSearchType === 'timeout') {
      dataList = _.cloneDeep(state.searchTimeoutDataList)
    }

    _.forEach(data, (value) => {
      const index = _.findIndex(dataList, {fw_rule_id: value.fw_rule_id})

      dataList.splice(index, 1)
    })

    if (state.editSearchType === 'contents') {
      state.searchContentDataList = Object.freeze(dataList)
    }

    if (state.editSearchType === 'timeout') {
      state.searchTimeoutDataList = Object.freeze(dataList)
    }
  },

  // 검색 결과 수정 벌크(정책 비활성화, 정책 삭제)
  updateBulkSearchData (state, data) {
    let dataList = []

    if (state.editSearchType === 'contents') {
      dataList = _.cloneDeep(state.searchContentDataList)
    }

    if (state.editSearchType === 'timeout') {
      dataList = _.cloneDeep(state.searchTimeoutDataList)
    }

    _.forEach(data.items, (value) => {
      const index = _.findIndex(dataList, {fw_rule_id: value})

      dataList.splice(index, 1)
    })

    if (state.editSearchType === 'contents') {
      state.searchContentDataList = Object.freeze(dataList)
    }

    if (state.editSearchType === 'timeout') {
      state.searchTimeoutDataList = Object.freeze(dataList)
    }
  },

  // 검색 데이터 일괄 변경
  setSearchAllChangeDisable (state, {data, type, filterData, isTAMS}) {
    let key = void 0

    if (type === 'contents') {
      key = 'searchContentDataList'
    }

    if (type === 'timeout') {
      key = 'searchTimeoutDataList'
    }

    const stateData = _.map(state[key], (obj) => {
      // TAMS
      if (obj.fw_set_id === 0 && isTAMS) {
        return obj
      }

      if (_.includes(filterData, obj)) {
        return _.assign(obj, {
          disableRule: data
        })
      }

      return obj
    })

    state[key] = Object.freeze(stateData)
  },

  // 만료 기준일 지정
  setExpiredDateChange (state, date) {
    state.searchTimeoutData.expired_date = formatDate(date, 'YYYY-MM-DD')
  },

  // 정책 생성일 시작 날짜 String으로 변경
  parseCreateDtStartDateData (state, date) {
    const
      fwEndExp = state.searchFwContentsData.create_dt.substring(8, 16),
      natEndExp = state.searchNatContentsData.create_dt.substring(8, 16)

    const
      fwResult = formatDate(date, 'YYYYMMDD') + fwEndExp,
      natResult = formatDate(date, 'YYYYMMDD') + natEndExp

    state.searchFwContentsData.create_dt = fwResult
    state.searchNatContentsData.create_dt = natResult
  },

  // 정책 생성일 종료 날짜 String으로 변경
  parseCreateDtEndDateData (state, date) {
    const
      fwStartExp = state.searchFwContentsData.create_dt.substring(0, 8),
      natStartExp = state.searchNatContentsData.create_dt.substring(0, 8)

    const
      fwResult = fwStartExp + formatDate(date, 'YYYYMMDD'),
      natResult = natStartExp + formatDate(date, 'YYYYMMDD')

    state.searchFwContentsData.create_dt = fwResult
    state.searchNatContentsData.create_dt = natResult
  },

  // 정책 내용 검색 - 정책 생성일 사용여부
  changeCreateDtCondition (state, is) {
    state.isPolicyContentCreateDt = is
  },

  // TAMS
  setEditSearchType (state, searchType) {
    state.editSearchType = searchType
  }
}

export const actions = {
  // 편집 정책 검색 - 방화벽 정책 내용 검색
  async searchFwContentsPolicy ({commit, state, rootState}) {
    // TAMS (cm)
    const version = rootState.cm.securityPolicy.firewall.firewallPolicySettings.fwRules.version
    let condition = state.searchFwContentsData

    if (!state.isPolicyContentCreateDt) {
      condition = _.omit(condition, ['create_dt'])
    }

    // TAMS
    if (!_.isEmpty(condition['fw_rule_id'])) {
      condition['fw_rule_id'] = Number(condition['fw_rule_id'])
    }
    condition['ip_ver_type'] = version === 4 ? 1 : 2
    if (state.searchCommonData.device_search_type === 1) {
      condition['device_id'] = _.map(state.searchCommonData.device_id, 'device_id')
    } else {
      condition = _.omit(condition, ['device_id'])
    }

    // const {code, result} = await this.$axios.$post(`${objApiPrefix}/fw/${version}/content-rules/search`, condition, {
    const res = await this.$axios.$post(`${cmApiPrefix}/fw-rule/search`, condition, {
      successAlert: false,
      loadingHide: false
    })

    if (res.code === 'ok') {
      commit('searchFwContentsPolicy', res.result)
    }
    return res
  },

  // 편집 정책 검색 - NAT 정책 내용 검색
  async searchNatContentsPolicy ({commit, state, rootState}) {
    const natVerApiPrefix = rootState.securityPolicy.nat.natPolicySettings.natRules.natVerApiPrefix
    let condition = state.searchNatContentsData

    if (!state.isPolicyContentCreateDt) {
      condition = _.omit(condition, ['create_dt'])
    }

    const {code, result} = await this.$axios.$post(`${objApiPrefix}/${natVerApiPrefix}/content-rules/search`, condition, {
      successAlert: false,
      loadingHide: false
    })

    if (code === 'ok') {
      commit('searchNatContentsPolicy', result)
      return result
    }
  },

  // 편집 정책 검색 - 시간 만료 검색
  async searchTimeoutPolicy ({commit, state, rootState}) {
    // TAMS (cm)
    const version = rootState.cm.securityPolicy.firewall.firewallPolicySettings.fwRules.version

    let searchCondition = Object.assign(_.cloneDeep(state.searchTimeoutData), {
      expired_date: formatDate(state.searchTimeoutData.expired_date, 'YYYY-MM-DD') // TAMS
    })

    // 만료 기한
    if (searchCondition.tm_type === 1) {
      searchCondition = _.pick(searchCondition, ['tm_type', 'expired', 'unuse_rule'])
    }

    // 만료 기준일
    if (searchCondition.tm_type === 2) {
      searchCondition = _.pick(searchCondition, ['tm_type', 'expired_date', 'unuse_rule'])
    }

    // TAMS
    searchCondition['ip_ver_type'] = version === 4 ? 1 : 2
    if (state.searchCommonData.device_search_type === 1) {
      searchCondition['device_id'] = _.map(state.searchCommonData.device_id, 'device_id')
    } else {
      searchCondition = _.omit(searchCondition, ['device_id'])
    }

    // const res = await this.$axios.$post(`${objApiPrefix}/fw/${version}/expire-rules/search`, searchCondition, {
    const res = await this.$axios.$post(`${cmApiPrefix}/fw-rule/expired`, searchCondition, {
      successAlert: false,
      loadingHide: false
    })

    if (res.code === 'ok') {
      commit('searchTimeoutPolicy', res.result)
    }
    return res
  },

  // 편집 정책 검색 - 방화벽 정책 shadow 정책 검색
  async searchFwShadowPolicy ({commit, state, rootState}, {shadowRuleCheck = false} = {}) {
    // TAMS (cm)
    const version = rootState.cm.securityPolicy.firewall.firewallPolicySettings.fwRules.version
    let searchFwShadowData = state.searchFwShadowData

    if (shadowRuleCheck) {
      searchFwShadowData = _.assign(searchFwShadowData, {
        fw_rule_id: void 0
      })
    }

    // TAMS 미사용
    // if (fwRuleId) {
    //   searchFwShadowData = _.assign(searchFwShadowData, {
    //     fw_rule_id: Number(fwRuleId)
    //   })
    // }

    // TAMS
    if (!_.isEmpty(searchFwShadowData['fw_rule_id'])) {
      searchFwShadowData['fw_rule_id'] = Number(searchFwShadowData['fw_rule_id'])
    }
    searchFwShadowData['ip_ver_type'] = version === 4 ? 1 : 2
    if (state.searchCommonData.device_search_type === 1) {
      searchFwShadowData['device_id'] = _.map(state.searchCommonData.device_id, 'device_id')
    } else {
      searchFwShadowData = _.omit(searchFwShadowData, ['device_id'])
    }

    // const res = await this.$axios.$post(`${objApiPrefix}/fw/${version}/shadow-rules/search`, searchFwShadowData, {
    const res = await this.$axios.$post(`${cmApiPrefix}/fw-rule/shadowed`, searchFwShadowData, {
      successAlert: false,
      loadingHide: false
    })

    if (res.code === 'ok') {
      commit('searchShadowPolicy', {result: res.result, ruleType: 'fw'})
    }
    return res
  },

  // 편집 정책 검색 - NAT 정책 shadow 정책 검색
  async searchNatShadowPolicy ({commit, state, rootState}, {natRuleId} = {}) {
    const natVerApiPrefix = rootState.securityPolicy.nat.natPolicySettings.natRules.natVerApiPrefix
    let searchNatShadowData = state.searchNatShadowData

    if (natRuleId) {
      searchNatShadowData = _.assign(searchNatShadowData, {
        nat_rule_id: natRuleId
      })
    }

    const {code, result} = await this.$axios.$post(`${objApiPrefix}/${natVerApiPrefix}/shadow-rules/search`, searchNatShadowData, {
      successAlert: false,
      loadingHide: false
    })

    if (code === 'ok') {
      commit('searchShadowPolicy', {result, ruleType: 'nat'})
      return result
    }
  },

  // 편집 정책 엑셀 다운로드 (비동기)
  async editSearchExcelDownload ({state}, {version, filterData}) {
    const id = _.compact(_.map(filterData, 'fw_rule_id'))
    let url = void 0

    if (state.editSearchType === 'contents') {
      url = `${objApiPrefix}/fw/${version}/content-rules/search/save`
    } else if (state.editSearchType === 'timeout') {
      url = `${objApiPrefix}/fw/${version}/expire-rules/search/save`
    } else if (state.editSearchType === 'shadow') {
      url = `${objApiPrefix}/fw/${version}/shadow-rules/search/save`
    }

    const {code, result} = await this.$axios.$post(url, {fw_rule_id: id})

    if (code === 'async') {
      return result.id
    }
  },

  // 엑셀 파일 다운로드
  async excelFileDownload ({state}, {taskId}) {
    const {data, fileName} = await this.$axios.$file(`${cmnApiPrefix}/async/tasks/${taskId}/save`, {responseType: 'blob'})
    pcDownloadFile(data, fileName)
  },

  // 검색 결과 수정(정책 비활성화, 정책 삭제)
  updateSearchData ({state, commit}, {batchValue}) {
    commit('updateSearchData', batchValue)
  },

  // 검색 결과 수정 벌크(정책 비활성화, 정책 삭제)
  updateBulkSearchData ({state, commit}, {batchValue}) {
    commit('updateBulkSearchData', batchValue)
  }
}

export const getters = {
  // NAT 정책 인터페이스 목록
  getApplyInterfaceList (state, getters, rootState) {
    const natApplyInterface = rootState.securityPolicy.nat.natPolicySettings.natRules.natApplyInterface

    return _.map(natApplyInterface, (inf) => {
      return {
        label: inf.name,
        value: inf.name
      }
    })
  }
}

export const strict = false
